/**
 * The API client.
 *
 * Types come from `schema.d.ts`, which is generated from `docs/api/openapi.json`.
 * Nothing here restates a field name by hand, so the compiler catches a contract
 * change rather than a teacher finding it.
 *
 * Regenerate after any route change:
 *   poetry run python scripts/dump_openapi.py
 *   npm run api:types
 */

import type { components } from './schema'

type Schemas = components['schemas']

// The wire shapes, re-exported under readable names so features import from here
// rather than reaching into the generated file.
export type Me = Schemas['MeResponse']
export type Session = Schemas['SessionResponse']
export type WorksheetSummary = Schemas['WorksheetSummary']
export type WorksheetDetail = Schemas['WorksheetDetail']
export type Question = Schemas['QuestionOut']
export type Section = Schemas['SectionOut']
export type MatchingPair = Schemas['MatchingPairIn']
export type QuestionType = Schemas['QuestionTypeOut']
export type Option = Schemas['OptionOut']
export type OptionIn = Schemas['OptionIn']
export type Preview = Schemas['PreviewResponse']
export type Page = Schemas['PageOut']
export type WorkflowState = Schemas['WorkflowStateResponse']
export type QueueItem = Schemas['QueueItem']
export type Comment = Schemas['CommentOut']
export type Person = Schemas['PersonOut']
export type Invite = Schemas['InviteOut']
export type Role = Schemas['RoleOut']
export type Image = Schemas['ImageOut']
export type ImagePage = Schemas['ImagePageResponse']
export type Template = Schemas['TemplateOut']
export type School = Schemas['SchoolOut']
export type PlatformSchool = Schemas['PlatformSchoolOut']
export type SandboxSchool = Schemas['SandboxSchoolResponse']
export type SchoolDeleted = Schemas['SchoolDeletedResponse']
export type OnboardedSchool = Schemas['OnboardSchoolResponse']
export type Branding = Schemas['BrandingStatusOut']
export type PrintRun = Schemas['PrintRun']
export type SheetType = Schemas['WorksheetSummary']['sheet_type']
export type AiStatus = Schemas['AiStatusResponse']
export type AiSchoolSettings = Schemas['AiSchoolSettingsOut']
export type SiteAiFeature = Schemas['SiteAiFeatureOut']
export type SchoolAiFeature = Schemas['SchoolAiFeatureOut']
export type GrammarSuggestion = Schemas['GrammarSuggestResponse']
export type ImageCandidate = Schemas['ImageCandidateOut']
export type ImageStyle = Schemas['ImageGenerateRequest']['style']
export type AiConnectionTest = Schemas['AiConnectionTestResponse']
export type AiUsageSummary = Schemas['AiUsageSummaryOut']
export type SiteRetention = Schemas['SiteRetentionOut']
export type SiteEmailSettings = Schemas['SiteEmailSettingsOut']
export type UpdateSiteEmailSettings = Schemas['UpdateSiteEmailSettingsRequest']
export type EmailTestResult = Schemas['EmailTestResponse']
export type EmailEvent = Schemas['EmailEventOut']
export type EmailTemplate = Schemas['EmailTemplateOut']
export type EmailOutboxEntry = Schemas['EmailOutboxEntryOut']
export type EmailOutboxPage = Schemas['EmailOutboxPageResponse']
export type SchoolEmailSettings = Schemas['SchoolEmailSettingsOut']
export type UpdateSchoolSmtp = Schemas['UpdateSchoolSmtpRequest']
export type SchoolRetention = Schemas['SchoolRetentionOut']
export type AuditEvent = Schemas['AuditEventOut']
export type AuditPage = Schemas['AuditPageResponse']

/** Not yet in the generated schema - added by the invite-preview endpoint. */
export type InvitePreview = {
  full_name: string
  email: string
  roles: string[]
  school_name: string
  expired: boolean
}

export type AuditFilters = {
  school_id?: string
  actor_id?: string
  action?: string
  q?: string
  from_at?: string
  to_at?: string
  page?: number
  per_page?: number
}

const BASE = import.meta.env.VITE_API_URL ?? window.location.origin

const TOKEN_KEY = 'tc.token'

/**
 * The signed session token.
 *
 * In `localStorage` so a refresh does not sign a teacher out mid-paper, which is
 * the single most irritating thing this class of application does. It is a bearer
 * token rather than a cookie, so there is no CSRF surface to defend.
 */
export const auth = {
  get(): string | null {
    return localStorage.getItem(TOKEN_KEY)
  },
  set(token: string) {
    localStorage.setItem(TOKEN_KEY, token)
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY)
  },
}

/**
 * A failed request, carrying the message the server wrote for a teacher.
 *
 * The server already phrases every refusal in words a teacher can act on, so the
 * UI shows `message` unchanged. Inventing our own wording here would mean two
 * clients describing the same failure differently.
 */
export class ApiError extends Error {
  // Declared as fields rather than constructor parameter properties: TypeScript's
  // `erasableSyntaxOnly` disallows the shorthand, because it is syntax that emits
  // code rather than being erased at compile time.
  readonly status: number
  readonly reference?: string

  constructor(status: number, message: string, reference?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.reference = reference
  }

  /** A 401 means the session is gone, and the app should return to sign-in. */
  get isSignedOut() {
    return this.status === 401
  }
}

type Options = {
  method?: string
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  /** Multipart uploads set their own content type, so the body is passed through. */
  form?: FormData
}

async function request<T>(path: string, options: Options = {}): Promise<T> {
  const url = new URL(`/api${path}`, BASE)
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value))
  }

  const headers: Record<string, string> = {}
  const token = auth.get()
  if (token) headers.Authorization = `Bearer ${token}`
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  const response = await fetch(url, {
    method: options.method ?? (options.body || options.form ? 'POST' : 'GET'),
    headers,
    body: options.form ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
  })

  if (!response.ok) {
    // A body is not guaranteed - a proxy can fail a request before it reaches us.
    const detail = await response.json().catch(() => ({}))
    throw new ApiError(
      response.status,
      detail.message ?? 'Something went wrong. Try again in a moment.',
      detail.reference,
    )
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

/** A file download, kept separate because the response is bytes, not JSON. */
async function download(
  path: string,
  query?: Options['query'],
  body?: unknown,
): Promise<Blob> {
  const url = new URL(`/api${path}`, BASE)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value))
  }
  const token = auth.get()
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const response = await fetch(url, {
    method: body === undefined ? 'GET' : 'POST',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}))
    throw new ApiError(response.status, detail.message ?? 'That file could not be downloaded.')
  }
  return response.blob()
}

const TIMESTAMPED_DOWNLOAD_EXTENSION = /^(.*?)(\.(?:pdf|ppt|pptx|doc|docx))$/i
const EXISTING_DOWNLOAD_TIMESTAMP = /_\d{8}_\d{6}$/

function twoDigits(value: number) {
  return String(value).padStart(2, '0')
}

/** Local browser time, matching the time the user sees when they download. */
export function downloadTimestamp(now: Date = new Date()) {
  return [
    now.getFullYear(),
    twoDigits(now.getMonth() + 1),
    twoDigits(now.getDate()),
    '_',
    twoDigits(now.getHours()),
    twoDigits(now.getMinutes()),
    twoDigits(now.getSeconds()),
  ].join('')
}

/** Timestamp exported PDF, PowerPoint and Word files immediately before their extension. */
export function timestampedDownloadFilename(filename: string, now: Date = new Date()) {
  const match = filename.match(TIMESTAMPED_DOWNLOAD_EXTENSION)
  if (!match) return filename

  const [, stem, extension] = match
  if (EXISTING_DOWNLOAD_TIMESTAMP.test(stem)) return filename
  return stem + '_' + downloadTimestamp(now) + extension
}

/** Hand the browser a blob as a named download. */
export function saveBlob(blob: Blob, filename: string) {
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = timestampedDownloadFilename(filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(href)
}

export const api = {
  // ------------------------------------------------------------------ auth
  signIn: (email: string, password: string) =>
    request<Session>('/auth/sign-in', { body: { email, password } }),

  acceptInvite: (token: string, full_name: string, password: string) =>
    request<Session>('/auth/accept-invite', { body: { token, full_name, password } }),

  invitePreview: (token: string) =>
    request<InvitePreview>('/auth/invite-preview', { query: { token } }),

  forgotPassword: (email: string) =>
    request<Schemas['ForgotPasswordResponse']>('/auth/forgot-password', {
      body: { email },
    }),

  resetPassword: (token: string, password: string) =>
    request<Schemas['Ok']>('/auth/reset-password', {
      body: { token, password },
    }),

  me: () => request<Me>('/auth/me'),

  /** Ends the session on the server, not only in this browser. */
  signOut: () => request<Schemas['Ok']>('/auth/sign-out', { method: 'POST' }),

  // --------------------------------------------------------------- audit
  auditEvents: (filters: AuditFilters = {}) =>
    request<AuditPage>('/audit-events', { query: filters }),

  exportAuditEvents: (filters: AuditFilters = {}) =>
    download('/audit-events/export', filters),

  // ------------------------------------------------------------ worksheets
  listWorksheets: (archived = false) =>
    request<WorksheetSummary[]>('/worksheets', { query: { archived } }),

  createWorksheet: (
    title: string,
    sheetType: SheetType = 'worksheet',
    headerTemplateId?: string,
  ) =>
    request<WorksheetSummary>('/worksheets', {
      body: {
        title,
        sheet_type: sheetType,
        header_template_id: headerTemplateId,
      },
    }),

  getWorksheet: (id: string) => request<WorksheetDetail>(`/worksheets/${id}`),

  updateWorksheet: (id: string, body: Partial<Schemas['UpdateWorksheetRequest']>) =>
    request<WorksheetSummary>(`/worksheets/${id}`, { method: 'PATCH', body }),

  deleteWorksheet: (id: string) => request<void>(`/worksheets/${id}`, { method: 'DELETE' }),

  archiveWorksheet: (id: string) =>
    request<WorksheetSummary>(`/worksheets/${id}/archive`, { method: 'POST' }),

  restoreWorksheet: (id: string) =>
    request<WorksheetSummary>(`/worksheets/${id}/restore`, { method: 'POST' }),

  duplicateWorksheet: (id: string) =>
    request<WorksheetSummary>(`/worksheets/${id}/duplicate`, { method: 'POST' }),

  updatePrintRun: (id: string, body: Schemas['UpdatePrintRunRequest']) =>
    request<PrintRun>(`/worksheets/${id}/print-run`, { method: 'PUT', body }),

  updateSection: (id: string, sectionId: string, body: Schemas['UpdateSectionRequest']) =>
    request<void>(`/worksheets/${id}/sections/${sectionId}`, { method: 'PUT', body }),

  // ------------------------------------------------------------- questions
  questionTypes: () => request<QuestionType[]>('/worksheets/question-types'),

  addQuestion: (
    worksheetId: string,
    type: string,
    sectionId?: string | null,
    passageId?: string | null,
  ) =>
    request<Question>(`/worksheets/${worksheetId}/questions`, {
      body: { type, section_id: sectionId ?? null, passage_id: passageId ?? null },
    }),

  // ---------------------------------------------------------------- sections
  addSection: (worksheetId: string, title = '') =>
    request<Section>(`/worksheets/${worksheetId}/sections`, { body: { title } }),

  deleteSection: (worksheetId: string, sectionId: string) =>
    request<void>(`/worksheets/${worksheetId}/sections/${sectionId}`, { method: 'DELETE' }),

  moveSection: (worksheetId: string, sectionId: string, position: number) =>
    request<void>(`/worksheets/${worksheetId}/sections/${sectionId}/move`, {
      body: { position },
    }),

  /** Matching pairs. The service stores them as options two at a time. */
  setMatchingPairs: (worksheetId: string, questionId: string, pairs: MatchingPair[]) =>
    request<Question>(`/worksheets/${worksheetId}/questions/${questionId}/pairs`, {
      method: 'PUT',
      body: { pairs },
    }),

  updateQuestion: (
    worksheetId: string,
    questionId: string,
    // Partial because every field has a server-side default; sending only what
    // changed is the point of PATCH.
    body: Partial<Schemas['UpdateQuestionRequest']>,
  ) =>
    request<Question>(`/worksheets/${worksheetId}/questions/${questionId}`, {
      method: 'PATCH',
      body,
    }),

  deleteQuestion: (worksheetId: string, questionId: string) =>
    request<void>(`/worksheets/${worksheetId}/questions/${questionId}`, { method: 'DELETE' }),

  moveQuestion: (worksheetId: string, questionId: string, position: number) =>
    request<void>(`/worksheets/${worksheetId}/questions/${questionId}/move`, {
      body: { position },
    }),

  duplicateQuestion: (worksheetId: string, questionId: string) =>
    request<Question>(`/worksheets/${worksheetId}/questions/${questionId}/duplicate`, {
      method: 'POST',
    }),

  setOptions: (worksheetId: string, questionId: string, options: OptionIn[]) =>
    request<Question>(`/worksheets/${worksheetId}/questions/${questionId}/options`, {
      method: 'PUT',
      body: { options },
    }),

  undo: (worksheetId: string) => request<{ ok: boolean }>(`/worksheets/${worksheetId}/undo`, { method: 'POST' }),
  redo: (worksheetId: string) => request<{ ok: boolean }>(`/worksheets/${worksheetId}/redo`, { method: 'POST' }),

  // --------------------------------------------------------------- preview
  preview: (worksheetId: string, includeAnswers = false) =>
    request<Preview>(`/worksheets/${worksheetId}/preview`, {
      query: { include_answers: includeAnswers },
    }),

  exportDocx: (worksheetId: string, includeAnswers = false) =>
    download(`/worksheets/${worksheetId}/export`, { include_answers: includeAnswers }),

  // ----------------------------------------------------------- portability
  exportWorksheetJson: (worksheetId: string) => download(`/worksheets/${worksheetId}/export-json`),

  importWorksheetJson: (content: string) =>
    request<Schemas['ImportWorksheetResponse']>('/worksheets/import-json', { body: { content } }),

  // -------------------------------------------------------------- workflow
  workflow: (worksheetId: string) => request<WorkflowState>(`/worksheets/${worksheetId}/workflow`),

  perform: (worksheetId: string, body: Schemas['PerformActionRequest']) =>
    request<WorkflowState>(`/worksheets/${worksheetId}/workflow`, { body }),

  reviewQueue: () => request<QueueItem[]>('/queues/review'),
  printQueue: () => request<QueueItem[]>('/queues/printing'),

  approvedFile: (worksheetId: string) => download(`/worksheets/${worksheetId}/approved-file`),

  /** Several approved papers as one zip. Serves the frozen artifacts, never a
   *  re-render, so a template edit after approval cannot change what prints. */
  batchDownload: async (worksheetIds: string[]) => {
    const url = new URL('/api/queues/printing/download', BASE)
    const token = auth.get()
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ worksheet_ids: worksheetIds }),
    })
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}))
      throw new ApiError(response.status, detail.message ?? 'Those files could not be downloaded.')
    }
    return response.blob()
  },

  uploadTemplateLogo: (templateId: string, file: File, schoolId?: string) => {
    const form = new FormData()
    form.append('file', file)
    return request<Template>(`/school/templates/${templateId}/logo`, {
      form,
      query: { school_id: schoolId },
    })
  },

  // -------------------------------------------------------------- comments
  comments: (worksheetId: string) => request<Comment[]>(`/worksheets/${worksheetId}/comments`),

  addComment: (worksheetId: string, body: string, question_id?: string | null) =>
    request<Comment>(`/worksheets/${worksheetId}/comments`, { body: { body, question_id } }),

  resolveComment: (worksheetId: string, commentId: string) =>
    request<void>(`/worksheets/${worksheetId}/comments/${commentId}/resolve`, { method: 'POST' }),

  // -------------------------------------------------------------- pictures
  pictures: (query: { q?: string; tags?: string; page?: number; per_page?: number } = {}) =>
    request<ImagePage>('/pictures', { query }),

  pictureTags: () => request<string[]>('/pictures/tags'),

  uploadPictures: (files: File[]) => {
    const form = new FormData()
    files.forEach((file) => form.append('files', file))
    return request<Image[]>('/pictures', { form })
  },

  importPicture: (url: string) => request<Image>('/pictures/import', { body: { url } }),

  setPictureTags: (id: string, tags: string[]) =>
    request<Image>(`/pictures/${id}/tags`, { method: 'PUT', body: { tags } }),

  pictureUsage: (id: string) => request<number>(`/pictures/${id}/usage`),

  deletePicture: (id: string) => request<void>(`/pictures/${id}`, { method: 'DELETE' }),

  /** A picture's bytes.
   *
   * Deliberately not exposed as a plain URL for an `<img src>`: the endpoint is
   * authenticated, and an `<img>` cannot carry an Authorization header, so that
   * would 401 on every screen. Use the `Picture` component, which fetches with
   * the header and hands the browser an object URL.
   */
  pictureBlob: (id: string) => download(`/pictures/${id}/file`),

  // ---------------------------------------------------------------- school
  school: (schoolId?: string) =>
    request<School>('/school', { query: { school_id: schoolId } }),
  updateSchool: (body: Schemas['UpdateSchoolRequest'], schoolId?: string) =>
    request<School>('/school', {
      method: 'PATCH',
      body,
      query: { school_id: schoolId },
    }),
  uploadSchoolLogo: (file: File, schoolId?: string) => {
    const form = new FormData()
    form.append('file', file)
    return request<School>('/school/logo', {
      form,
      query: { school_id: schoolId },
    })
  },
  clearSchoolLogo: (schoolId?: string) =>
    request<School>('/school/logo', {
      method: 'DELETE',
      query: { school_id: schoolId },
    }),

  templates: (schoolId?: string) =>
    request<Template[]>('/school/templates', { query: { school_id: schoolId } }),
  templatePresets: () => request<string[]>('/school/template-presets'),
  createTemplate: (body: Schemas['CreateTemplateRequest'], schoolId?: string) =>
    request<Template>('/school/templates', {
      body,
      query: { school_id: schoolId },
    }),
  updateTemplate: (id: string, body: Schemas['UpdateTemplateRequest'], schoolId?: string) =>
    request<Template>(`/school/templates/${id}`, {
      method: 'PATCH',
      body,
      query: { school_id: schoolId },
    }),
  templatePreview: (
    id: string,
    format: 'docx' | 'pdf',
    body: Schemas['TemplatePreviewRequest'],
    schoolId?: string,
  ) =>
    download(
      `/school/templates/${id}/preview/${format}`,
      { school_id: schoolId },
      body,
    ),
  setDefaultTemplate: (id: string, schoolId?: string) =>
    request<void>(`/school/templates/${id}/default`, {
      method: 'POST',
      query: { school_id: schoolId },
    }),
  deleteTemplate: (id: string, schoolId?: string) =>
    request<void>(`/school/templates/${id}`, {
      method: 'DELETE',
      query: { school_id: schoolId },
    }),

  people: (schoolId?: string) =>
    request<Person[]>('/school/people', { query: { school_id: schoolId } }),
  /** Every role: its teacher-facing label, and whether it can be given out. */
  roles: () => request<Record<string, Role>>('/school/roles'),
  updatePerson: (
    personId: string,
    body: { full_name: string; roles: string[] },
    schoolId?: string,
  ) =>
    request<Person>(`/school/people/${personId}`, {
      method: 'PUT',
      body,
      query: { school_id: schoolId },
    }),
  setRoles: (personId: string, roles: string[], schoolId?: string) =>
    request<Person>(`/school/people/${personId}/roles`, {
      method: 'PUT',
      body: { roles },
      query: { school_id: schoolId },
    }),
  setActive: (personId: string, is_active: boolean, schoolId?: string) =>
    request<Person>(`/school/people/${personId}/active`, {
      method: 'PUT',
      body: { is_active },
      query: { school_id: schoolId },
    }),
  setDefaultReviewer: (reviewer_id: string | null, schoolId?: string) =>
    request<void>('/school/default-reviewer', {
      method: 'PUT',
      body: { reviewer_id },
      query: { school_id: schoolId },
    }),

  invitations: (schoolId?: string) =>
    request<Invite[]>('/school/invitations', { query: { school_id: schoolId } }),
  invite: (body: Schemas['InviteRequest'], schoolId?: string) =>
    request<Invite>('/school/invitations', {
      body,
      query: { school_id: schoolId },
    }),
  cancelInvitation: (id: string, schoolId?: string) =>
    request<void>(`/school/invitations/${id}`, {
      method: 'DELETE',
      query: { school_id: schoolId },
    }),

  // ------------------------------------------------------------ platform
  platformSchools: () => request<PlatformSchool[]>('/platform/schools'),
  onboardSchool: (body: Schemas['OnboardSchoolRequest']) =>
    request<OnboardedSchool>('/platform/schools', { body }),

  /** A throwaway school for reproducing a reported problem. */
  createSandbox: (name = '') =>
    request<SandboxSchool>('/platform/schools/sandbox', { body: { name } }),

  /** Destroys a sandbox and everything in it. Refused for any real school. */
  deleteSchool: (schoolId: string) =>
    request<SchoolDeleted>(`/platform/schools/${schoolId}`, { method: 'DELETE' }),

  /** Offboard a school, or bring it back. Nothing is destroyed either way. */
  setSchoolActive: (schoolId: string, is_active: boolean) =>
    request<PlatformSchool>(`/platform/schools/${schoolId}/active`, {
      method: 'PUT',
      body: { is_active },
    }),
  branding: () => request<Branding>('/branding'),
  uploadBranding: (kind: 'logo' | 'favicon', file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<Branding>(`/platform/branding/${kind}`, { form })
  },
  restoreBranding: (kind: 'logo' | 'favicon') =>
    request<Branding>(`/platform/branding/${kind}`, { method: 'DELETE' }),

  siteRetention: () => request<SiteRetention>('/platform/retention'),
  updateSiteRetention: (max_days: number) =>
    request<SiteRetention>('/platform/retention', {
      method: 'PUT',
      body: { max_days },
    }),

  // ----------------------------------------------------------------- email
  siteEmailSettings: () => request<SiteEmailSettings>('/platform/email/settings'),
  // `smtp_password` omitted keeps the stored password; '' clears it.
  updateSiteEmailSettings: (body: UpdateSiteEmailSettings) =>
    request<SiteEmailSettings>('/platform/email/settings', { method: 'PUT', body }),
  // Takes no address: the server only ever mails the signed-in administrator.
  // `to` empty means the signed-in administrator's own address.
  sendTestEmail: (to = '') =>
    request<EmailTestResult>('/platform/email/test', { method: 'POST', body: { to } }),

  emailEvents: () => request<EmailEvent[]>('/platform/email/events'),
  setEmailEventEnabled: (key: string, enabled: boolean) =>
    request<EmailEvent>(`/platform/email/events/${key}/enabled`, {
      method: 'PUT',
      body: { enabled },
    }),
  emailTemplate: (key: string) => request<EmailTemplate>(`/platform/email/templates/${key}`),
  updateEmailTemplate: (key: string, body: Schemas['UpdateEmailTemplateRequest']) =>
    request<EmailTemplate>(`/platform/email/templates/${key}`, { method: 'PUT', body }),
  // Reverts the wording only; the on/off switch is left as it is.
  resetEmailTemplate: (key: string) =>
    request<EmailTemplate>(`/platform/email/templates/${key}`, { method: 'DELETE' }),
  previewEmailTemplate: (key: string) =>
    request<EmailTemplate>(`/platform/email/templates/${key}/preview`, { method: 'POST' }),

  emailOutbox: (params: { status?: string; page?: number } = {}) =>
    request<EmailOutboxPage>('/platform/email/outbox', {
      query: { status: params.status, page: params.page },
    }),
  retryEmail: (id: string) =>
    request<EmailOutboxEntry>(`/platform/email/outbox/${id}/retry`, { method: 'POST' }),

  // The site administrator's grants, per school.
  schoolEmailPermissions: (schoolId: string) =>
    request<SchoolEmailSettings>(`/platform/schools/${schoolId}/email/permissions`),
  setSchoolEmailPermissions: (
    schoolId: string,
    body: { allow_custom_smtp: boolean; allow_custom_templates: boolean },
  ) =>
    request<SchoolEmailSettings>(`/platform/schools/${schoolId}/email/permissions`, {
      method: 'PUT',
      body,
    }),

  // A school's own mail. Every call takes ?school_id= so a site administrator
  // can work on a school's behalf, exactly like the rest of the school API.
  schoolEmail: (schoolId?: string) =>
    request<SchoolEmailSettings>('/school/email', { query: { school_id: schoolId } }),
  updateSchoolSmtp: (body: UpdateSchoolSmtp, schoolId?: string) =>
    request<SchoolEmailSettings>('/school/email/smtp', {
      method: 'PUT',
      body,
      query: { school_id: schoolId },
    }),
  setSchoolUseCustomSmtp: (use_custom: boolean, schoolId?: string) =>
    request<SchoolEmailSettings>('/school/email/use-custom-smtp', {
      method: 'PUT',
      body: { use_custom },
      query: { school_id: schoolId },
    }),
  setSchoolUseCustomTemplates: (use_custom: boolean, schoolId?: string) =>
    request<SchoolEmailSettings>('/school/email/use-custom-templates', {
      method: 'PUT',
      body: { use_custom },
      query: { school_id: schoolId },
    }),
  sendSchoolTestEmail: (schoolId?: string, to = '') =>
    request<EmailTestResult>('/school/email/test', {
      method: 'POST',
      body: { to },
      query: { school_id: schoolId },
    }),

  schoolEmailEvents: (schoolId?: string) =>
    request<EmailEvent[]>('/school/email/events', { query: { school_id: schoolId } }),
  // `null` means "follow the platform" rather than "off".
  setSchoolEmailEventEnabled: (key: string, enabled: boolean | null, schoolId?: string) =>
    request<EmailEvent>(`/school/email/events/${key}/enabled`, {
      method: 'PUT',
      body: { enabled },
      query: { school_id: schoolId },
    }),
  schoolEmailTemplate: (key: string, schoolId?: string) =>
    request<EmailTemplate>(`/school/email/templates/${key}`, {
      query: { school_id: schoolId },
    }),
  updateSchoolEmailTemplate: (
    key: string,
    body: Schemas['UpdateEmailTemplateRequest'],
    schoolId?: string,
  ) =>
    request<EmailTemplate>(`/school/email/templates/${key}`, {
      method: 'PUT',
      body,
      query: { school_id: schoolId },
    }),
  resetSchoolEmailTemplate: (key: string, schoolId?: string) =>
    request<EmailTemplate>(`/school/email/templates/${key}`, {
      method: 'DELETE',
      query: { school_id: schoolId },
    }),
  previewSchoolEmailTemplate: (key: string, schoolId?: string) =>
    request<EmailTemplate>(`/school/email/templates/${key}/preview`, {
      method: 'POST',
      query: { school_id: schoolId },
    }),

  schoolRetention: (schoolId?: string) =>
    request<SchoolRetention>('/school/retention', { query: { school_id: schoolId } }),
  updateSchoolRetention: (retention_days: number, schoolId?: string) =>
    request<SchoolRetention>('/school/retention', {
      method: 'PUT',
      body: { retention_days },
      query: { school_id: schoolId },
    }),

  // -------------------------------------------------------------------- ai
  aiStatus: () => request<AiStatus>('/ai/status'),
  suggestGrammar: (text: string) =>
    request<GrammarSuggestion>('/ai/grammar/suggest', { body: { text } }),
  acceptGrammar: (usage_event_id: string) =>
    request<Schemas['Ok']>('/ai/grammar/accept', { body: { usage_event_id } }),
  generateImage: (prompt: string, style: ImageStyle = 'auto') =>
    request<ImageCandidate>('/ai/image/generate', { body: { prompt, style } }),
  acceptImage: (usage_event_id: string, prompt: string, data_url: string) =>
    request<Image>('/ai/image/accept', { body: { usage_event_id, prompt, data_url } }),

  // -------------------------------------------------------- platform: ai
  /** The deployment's AI feature catalogue. Readable by a school administrator
   *  too: their own panel only shows switches for features the site offers. */
  siteAiFeatures: () => request<SiteAiFeature[]>('/platform/ai/features'),
  /** Site administrator only - what this deployment puts on the shelf. */
  setSiteAiFeature: (key: string, enabled: boolean) =>
    request<SiteAiFeature[]>(`/platform/ai/features/${key}`, {
      method: 'PUT',
      body: { enabled },
    }),
  /** Try a feature's provider once, without involving any school. A key sent
   *  here is used for that single call and never stored. */
  testSiteAiFeature: (
    key: string,
    body: {
      mode: 'subscription' | 'byok'
      provider?: string | null
      api_key?: string | null
      base_url?: string | null
      model?: string | null
    },
  ) => request<AiConnectionTest>(`/platform/ai/features/${key}/test`, { body }),
  schoolAiSettings: (schoolId: string) =>
    request<AiSchoolSettings>(`/platform/schools/${schoolId}/ai`),
  /** The platform's suspension switch. Site administrator only, and not the
   *  switch a school waits on - that is `setSchoolAiEnabled`. */
  setAiEnabled: (schoolId: string, enabled: boolean) =>
    request<AiSchoolSettings>(`/platform/schools/${schoolId}/ai/enabled`, {
      method: 'PUT',
      body: { enabled },
    }),
  /** The school's own master switch (DR-029). */
  setSchoolAiEnabled: (schoolId: string, enabled: boolean) =>
    request<AiSchoolSettings>(`/platform/schools/${schoolId}/ai/school-enabled`, {
      method: 'PUT',
      body: { enabled },
    }),
  setSchoolAiFeature: (schoolId: string, key: string, enabled: boolean) =>
    request<AiSchoolSettings>(`/platform/schools/${schoolId}/ai/features/${key}`, {
      method: 'PUT',
      body: { enabled },
    }),
  setAiAccessMode: (schoolId: string, access_mode: 'subscription' | 'byok') =>
    request<AiSchoolSettings>(`/platform/schools/${schoolId}/ai/mode`, {
      method: 'PUT',
      body: { access_mode },
    }),
  setAiByok: (
    schoolId: string,
    body: {
      text_provider: string | null
      image_provider: string | null
      /** Blank or omitted keeps the key already saved on the server. */
      api_key?: string | null
      base_url?: string | null
      /** Blank means the server's built-in default model. */
      text_model?: string | null
      image_model?: string | null
    },
  ) =>
    request<AiSchoolSettings>(`/platform/schools/${schoolId}/ai/byok`, {
      method: 'PUT',
      body,
    }),
  clearAiByok: (schoolId: string) =>
    request<AiSchoolSettings>(`/platform/schools/${schoolId}/ai/byok`, { method: 'DELETE' }),
  setAiImageLimit: (schoolId: string, max_generated_image_px: number) =>
    request<AiSchoolSettings>(`/platform/schools/${schoolId}/ai/image-limit`, {
      method: 'PUT',
      body: { max_generated_image_px },
    }),
  setAiQuota: (schoolId: string, monthly_image_quota: number, monthly_text_quota: number) =>
    request<AiSchoolSettings>(`/platform/schools/${schoolId}/ai/quota`, {
      method: 'PUT',
      body: { monthly_image_quota, monthly_text_quota },
    }),
  setAiPrompts: (
    schoolId: string,
    grammar_prompt_override: string | null,
    image_prompt_prefix: string | null,
  ) =>
    request<AiSchoolSettings>(`/platform/schools/${schoolId}/ai/prompts`, {
      method: 'PUT',
      body: { grammar_prompt_override, image_prompt_prefix },
    }),
  testAiConnection: (schoolId: string, feature: 'grammar' | 'image') =>
    request<AiConnectionTest>(`/platform/schools/${schoolId}/ai/test-connection`, {
      body: { feature },
    }),
  aiUsage: (schoolId: string) =>
    request<AiUsageSummary>(`/platform/schools/${schoolId}/ai/usage`),

  // -------------------------------------------------------------- billing
  startCheckout: (schoolId?: string) =>
    request<Schemas['BillingCheckoutResponse']>('/billing/checkout', {
      method: 'POST',
      query: { school_id: schoolId },
    }),
  billingPortal: (schoolId?: string) =>
    request<Schemas['BillingCheckoutResponse']>('/billing/portal', {
      method: 'POST',
      query: { school_id: schoolId },
    }),
  cancelSubscription: (schoolId?: string) =>
    request<Schemas['BillingActionResponse']>('/billing/cancel', {
      method: 'POST',
      query: { school_id: schoolId },
    }),
  mockBillingSession: (token: string) =>
    request<Schemas['MockBillingSessionResponse']>('/billing/mock/session', {
      query: { token },
    }),
  completeMockBilling: (
    token: string,
    outcome: 'success' | 'failed' | 'cancelled',
  ) =>
    request<Schemas['MockBillingSessionResponse']>('/billing/mock/complete', {
      body: { token, outcome },
    }),
}
